import React, { useCallback, useState } from 'react';

import { CategoryID } from '../../../../categories/categoriesTypes';
import { TeamNanoID } from '../../../../teams/teamsTypes';

import { ItemsFilters, UUID } from '../../../../../types';
import { IconsEnum } from '../../../../../assets/icons/types';

import { ThreeDStockMenuProductsCategoriesNav } from '../ThreeDStockMenuProductsCategoriesNav';
import { ThreeDStockMenuLifestylesCategoriesNav } from '../ThreeDStockMenuLifestylesCategoriesNav';
import { ThreeDStockMenuMaterialsCategoriesNav } from '../ThreeDStockMenuMaterialsCategoriesNav';

import { PureIconButtonHelper } from '../../../../../helpers/buttons/PureIconButtonHelper';
import { CheckPermissions } from '../../../../../helpers/CheckPermissions';
import { NextLinkHelper } from '../../../../../helpers/links/NextLinkHelper';
import { Translate } from '../../../../../helpers/Translate';

import { StockPath } from '../../../StockPath';
import { StockPermissions, StockTabs } from '../../../stockConstants';

import { TeamPath } from '../../../../teams/TeamPath';
import { CommonPermissions } from '../../../../common/commonConstants';

import { words } from '../../../../../locales/keys';

const getCategoryTitleHref = (
  tab: StockTabs,
  isMyLibrary: boolean,
  companyNanoId: TeamNanoID
) => {
  const links = {
    [StockTabs.PRODUCTS]: {
      defaultHref: StockPath.newProducts(),
      myLybHref: isMyLibrary
        ? TeamPath.currentCompanyNewLibraryProducts()
        : null,
      companyHref: companyNanoId
        ? TeamPath.companyNewLibraryProducts(companyNanoId)
        : null
    },
    [StockTabs.LIFESTYLES]: {
      defaultHref: StockPath.newLifestyles(),
      myLybHref: isMyLibrary
        ? TeamPath.currentCompanyNewLibraryLifestyles()
        : null,
      companyHref: companyNanoId
        ? TeamPath.companyNewLibraryLifestyles(companyNanoId)
        : null
    },
    [StockTabs.MATERIALS]: {
      tab: StockTabs.MATERIALS,
      defaultHref: StockPath.newMaterials(),
      myLybHref: isMyLibrary
        ? TeamPath.currentCompanyNewLibraryMaterials()
        : null,
      companyHref: companyNanoId
        ? TeamPath.companyNewLibraryMaterials(companyNanoId)
        : null
    }
  };

  const link = links[tab];

  return link.myLybHref || link.companyHref || link.defaultHref;
};

const defaultShowMenus = [
  StockTabs.PRODUCTS,
  StockTabs.LIFESTYLES,
  StockTabs.MATERIALS
];

interface ThreeDStockMenuProps {
  onClose: () => void;
  changeItemsCategoryFilters?: (
    tab?: StockTabs,
    changeFilters?: ItemsFilters,
    removeFilters?: string[]
  ) => void;
  showTab?: StockTabs;
  isMyLibrary?: boolean;
  companyNanoId?: TeamNanoID;
  isModal?: boolean;
  clientIds?: string[];
  teamIds?: UUID[];
}

function ThreeDStockMenu({
  onClose,
  changeItemsCategoryFilters,
  showTab,
  companyNanoId,
  isMyLibrary,
  isModal,
  clientIds,
  teamIds
}: ThreeDStockMenuProps) {
  const [showMenus, setShowMenus] = useState<StockTabs[]>(
    showTab ? [showTab] : defaultShowMenus
  );

  const handleSelectCategory = useCallback(
    (menu: StockTabs) => (id: CategoryID) => {
      if (!showTab) {
        setShowMenus(id ? [menu] : defaultShowMenus);
      }
    },
    [showTab]
  );

  const defaultHref =
    (isMyLibrary || !companyNanoId) && StockPath.newProducts();

  const myLybHref =
    (isMyLibrary || (!isMyLibrary && !companyNanoId)) &&
    TeamPath.currentCompanyNewLibraryProducts();

  const companyHref =
    !!companyNanoId &&
    !isMyLibrary &&
    TeamPath.companyNewLibraryProducts(companyNanoId);

  return (
    <div
      id="all-menu-panel"
      className="absolute top-0 right-0 w-full h-screen bg-black/80 z-50"
    >
      <div className="bg-gray-50 dark:bg-gray-850 w-96 h-full flex flex-col">
        <div className="bg-gray-50 dark:bg-gray-850 w-96 h-full flex flex-col">
          <div className="pl-6 sm:pl-8 pr-4 h-16 flex justify-between items-center border-b dark:border-gray-700">
            {isModal ? (
              <div />
            ) : (
              <div className="flex items-center -mx-4">
                {defaultHref && (
                  <CheckPermissions
                    action={
                      CommonPermissions.READ_COMPACT_NEW_STOCK_MENU_PRODUCTS_LINK
                    }
                  >
                    <NextLinkHelper
                      href={defaultHref}
                      dataGa="three-d-stock-link"
                      className="relative text-lg font-bold px-4 py-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-150 ease-out"
                      onClick={onClose}
                      disabled={!myLybHref}
                    >
                      <Translate id={words.threeDStock} />

                      {!isMyLibrary && !companyNanoId && (
                        <span className="-bottom-2.5 absolute bg-blue-500 h-1 left-0 w-full" />
                      )}
                    </NextLinkHelper>
                  </CheckPermissions>
                )}

                {myLybHref && (
                  <CheckPermissions
                    action={
                      CommonPermissions.READ_COMPACT_SELF_COMPANY_NEW_LIBRARY_MENU_PRODUCTS_LINK
                    }
                  >
                    <NextLinkHelper
                      href={myLybHref}
                      dataGa="three-d-stock-link"
                      className="relative text-lg font-bold px-4 py-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-150 ease-out"
                      onClick={onClose}
                      disabled={!defaultHref}
                    >
                      <Translate id={words.myLibrary} />

                      {isMyLibrary && (
                        <span className="-bottom-2.5 absolute bg-blue-500 h-1 left-0 w-full" />
                      )}
                    </NextLinkHelper>
                  </CheckPermissions>
                )}

                {companyHref && (
                  <CheckPermissions
                    action={
                      CommonPermissions.READ_COMPACT_OTHER_COMPANY_NEW_LIBRARY_MENU_PRODUCTS_LINK
                    }
                  >
                    <NextLinkHelper
                      href={companyHref}
                      dataGa="three-d-stock-link"
                      className="relative text-lg font-bold px-4 py-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-150 ease-out"
                      onClick={onClose}
                      disabled={!defaultHref}
                    >
                      <Translate id={words.myLibrary} />

                      {companyNanoId && (
                        <span className="-bottom-2.5 absolute bg-blue-500 h-1 left-0 w-full" />
                      )}
                    </NextLinkHelper>
                  </CheckPermissions>
                )}
              </div>
            )}

            <PureIconButtonHelper
              className="py-2 pl-2 pr-2 rounded-full inline-flex items-center leading-6 focus:ring-base hover:text-gray-950 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 focus:ring-offset-0 transition-colors duration-150 ease-out"
              icon={IconsEnum.X_OUTLINE}
              iconClassName="h-6 w-6"
              onClick={onClose}
            />
          </div>

          <div
            key="showMenus"
            className="flex-1 overflow-y-auto px-2 md:px-4 divide-y divide-gray-200 dark:divide-gray-700"
          >
            {showMenus.map((menu) => {
              if (menu === StockTabs.PRODUCTS) {
                return (
                  <CheckPermissions
                    key={menu}
                    action={
                      StockPermissions.READ_THREE_D_STOCK_ALL_PRODUCTS_CATEGORIES
                    }
                  >
                    <ThreeDStockMenuProductsCategoriesNav
                      categoryTitleHref={getCategoryTitleHref(
                        StockTabs.PRODUCTS,
                        isMyLibrary,
                        companyNanoId
                      )}
                      disabledTitleLink={isModal}
                      onClose={onClose}
                      onSelectCategory={handleSelectCategory(
                        StockTabs.PRODUCTS
                      )}
                      changeItemsCategoryFilters={changeItemsCategoryFilters}
                      alwaysShowAllCategories={!!showTab}
                      clientIds={clientIds}
                      teamIds={teamIds}
                    />
                  </CheckPermissions>
                );
              }

              if (menu === StockTabs.LIFESTYLES) {
                return (
                  <CheckPermissions
                    key={menu}
                    action={
                      StockPermissions.READ_THREE_D_STOCK_ALL_LIFESTYLES_CATEGORIES
                    }
                  >
                    <ThreeDStockMenuLifestylesCategoriesNav
                      categoryTitleHref={getCategoryTitleHref(
                        StockTabs.LIFESTYLES,
                        isMyLibrary,
                        companyNanoId
                      )}
                      disabledTitleLink={isModal}
                      onClose={onClose}
                      onSelectCategory={handleSelectCategory(
                        StockTabs.LIFESTYLES
                      )}
                      changeItemsCategoryFilters={changeItemsCategoryFilters}
                      alwaysShowAllCategories={!!showTab}
                      clientIds={clientIds}
                    />
                    {/* TODO: add teamIds */}
                  </CheckPermissions>
                );
              }

              if (menu === StockTabs.MATERIALS) {
                return (
                  <CheckPermissions
                    key={menu}
                    action={
                      StockPermissions.READ_THREE_D_STOCK_ALL_MATERIALS_CATEGORIES
                    }
                  >
                    <ThreeDStockMenuMaterialsCategoriesNav
                      categoryTitleHref={getCategoryTitleHref(
                        StockTabs.MATERIALS,
                        isMyLibrary,
                        companyNanoId
                      )}
                      disabledTitleLink={isModal}
                      onClose={onClose}
                      onSelectCategory={handleSelectCategory(
                        StockTabs.MATERIALS
                      )}
                      changeItemsCategoryFilters={changeItemsCategoryFilters}
                      alwaysShowAllCategories={!!showTab}
                      clientIds={clientIds}
                    />
                    {/* TODO: add teamIds */}
                  </CheckPermissions>
                );
              }
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ThreeDStockMenu;
